import React, { useState } from 'react';
import { CloseX, Envelope, ArrowRightThin } from './vd/LineArt';
import { Link } from 'react-router-dom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable inline email form for the brochure.
 * variant: "block" (in-page) or "popup".
 */
export const BrochureForm = ({ variant = 'block', onSuccess, className = '' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Vnesi veljaven e-poštni naslov.');
      return;
    }
    if (!consent) {
      setError('Potrdi soglasje za prejemanje priročnika.');
      return;
    }
    setState('loading');
    try {
      const r = await fetch(`${BACKEND}/api/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, source: variant, send_brochure: true }),
      });
      if (!r.ok) throw new Error('fail');
      setState('success');
      onSuccess?.();
    } catch (err) {
      setState('error');
      setError('Nekaj je šlo narobe. Poskusi znova čez trenutek.');
    }
  };

  if (state === 'success') {
  return (
    <div
      className={`p-6 rounded-2xl bg-vd-primary-soft border border-vd-soft space-y-3 ${className}`}
      data-testid="brochure-success"
    >
      <p className="text-vd-text text-base leading-relaxed font-medium">
        Hvala. Priročnik je na poti v tvoj e-poštni nabiralnik.
      </p>

      <p className="text-vd-muted text-sm leading-relaxed">
        V nekaj trenutkih boš prejela e-mail z gumbom za dostop do priročnika.
        Če ga ne najdeš, preveri tudi mapo z neželeno pošto.
      </p>
    </div>
  );
}

  return (
    <form onSubmit={submit} className={`space-y-3 ${className}`} data-testid={`brochure-form-${variant}`} noValidate>
      <div>
        <label className="block text-xs text-vd-muted mb-1.5">Ime (neobvezno)</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-white border border-vd-soft rounded-lg px-4 py-2.5 text-sm text-vd-text focus:outline-none focus:border-vd-accent transition-colors"
          data-testid="brochure-name"
        />
      </div>
      <div>
        <label className="block text-xs text-vd-muted mb-1.5">E-mail *</label>
        <div className="relative">
          <Envelope size={16} className="absolute left-3 top-3 text-vd-muted" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full bg-white border border-vd-soft rounded-lg pl-9 pr-4 py-2.5 text-sm text-vd-text focus:outline-none focus:border-vd-accent transition-colors"
            data-testid="brochure-email"
          />
        </div>
      </div>
      <label className="flex items-start gap-2.5 text-xs text-vd-muted leading-relaxed cursor-pointer">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 accent-vd-accent"
          data-testid="brochure-consent"
        />
        <span>
          Strinjam se z obdelavo mojega e-poštnega naslova za pošiljanje priročnika in občasnih vsebin Val Dotika. Odjava je mogoča kadarkoli. Preberi{' '}
          <Link to="/politika-zasebnosti" className="underline text-vd-accent">politiko zasebnosti</Link>.
        </span>
      </label>
      {error && <p className="text-xs text-red-700" data-testid="brochure-error">{error}</p>}
      <button
        type="submit"
        disabled={state === 'loading'}
        className="w-full inline-flex items-center justify-center gap-2 bg-vd-accent text-white rounded-full py-3 text-sm font-medium hover:opacity-90 transition-all disabled:opacity-50"
        data-testid="brochure-submit"
      >
        {state === 'loading' ? 'Pošiljam …' : 'Prejmi brezplačni priročnik'}
        {state !== 'loading' && <ArrowRightThin size={14} />}
      </button>
    </form>
  );
};

const BrochurePopup = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true" data-testid="brochure-popup">
      <div className="absolute inset-0 bg-vd-text/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-vd-bg rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 vd-fade border border-vd-soft">
        <button onClick={onClose} className="absolute top-3 right-3 text-vd-muted hover:text-vd-text p-2" aria-label="Zapri" data-testid="brochure-close">
          <CloseX size={18} />
        </button>
        <h3 className="font-serif text-2xl sm:text-3xl text-vd-text mb-2">Brezplačni priročnik</h3>
        <p className="text-sm text-vd-muted leading-relaxed mb-5">
          Ali to delam prav? Če se ti je to vprašanje že kdaj podilo po glavi, potem je ta priročnik napisan zate. Vpiši svoj e-mail in ga brezplačno prejmi v svoj nabiralnik.
        </p>
        <BrochureForm variant="popup" onSuccess={() => setTimeout(onClose, 3500)} />
      </div>
    </div>
  );
};

export default BrochurePopup;
